import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useServiceCases } from "@/lib/queries";
import { SERVICE_STATUS_LABELS, SERVICE_TYPE_LABELS, BILLABLE_LABELS, weekLabel, formatDate } from "@/lib/format";
import { useScrollRestore } from "@/lib/hooks";
import { PageHeader, LoadingBlock, ErrorBlock, EmptyState } from "@/components/common/Blocks";
import { StateChip, toneForServiceStatus } from "@/components/common/Badges";

const SEG = [["apne", "Åpne"], ["alle", "Alle"], ["lukket", "Lukket"]];

export default function ServicePage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get("status") || "apne";
  const deliveryFilter = params.get("leveranse");
  const { data, isLoading, isError, refetch } = useServiceCases(status);
  useScrollRestore("scroll:/service", !!data);
  const rows = (data || []).filter((s) => !deliveryFilter || s.delivery_id === deliveryFilter);
  const setStatus = (v) => setParams((p) => { const n = new URLSearchParams(p); if (v === "apne") n.delete("status"); else n.set("status", v); return n; }, { replace: true });
  const open = (s) => navigate(`/service/${s.id}`);

  return (
    <div className="page" data-testid="service-page">
      <PageHeader title="Service" meta={data ? `${rows.length} serviceoppdrag · restpunkter, skader og reklamasjoner knyttet til leverte trapper` : null} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="seg" role="group" aria-label="Status" data-testid="service-status-filter">
          {SEG.map(([k, l]) => <button key={k} type="button" aria-pressed={status === k} onClick={() => setStatus(k)} data-testid={`service-filter-${k}`}>{l}</button>)}
        </div>
        {deliveryFilter && <p className="t-meta">Viser oppdrag for én leveranse. <Link to="/service" className="link">Vis alle</Link></p>}
      </div>
      {isLoading ? <LoadingBlock /> : isError ? <ErrorBlock onRetry={refetch} /> : rows.length === 0 ? <EmptyState title="Ingen serviceoppdrag" description="Restpunkter fra montasjerapporter dukker opp her." /> : (
        <div className="frame overflow-x-auto"><table className="data-table" data-testid="service-table">
          <thead>
            <tr>
              <th className="w-[32%] min-w-[200px]">Oppdrag</th>
              <th className="hidden md:table-cell">Leveranse</th>
              <th className="hidden w-[170px] sm:table-cell">Avtalt</th>
              <th className="w-[120px]">Status</th>
              <th className="hidden w-[140px] xl:table-cell">Fakturering</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="is-link" tabIndex={0} role="link" onClick={() => open(s)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(s); } }} data-testid={`service-row-${s.number}`}>
                <td>
                  <p className="text-[15px] font-semibold leading-snug" data-testid={`open-service-${s.number}`}>{s.title}</p>
                  <p className="t-meta mt-0.5">{s.number} · {SERVICE_TYPE_LABELS[s.type] || s.type} · meldt {formatDate(s.created_at)}</p>
                  <p className="t-meta mt-0.5 md:hidden">{s.project_name} · {s.customer_name}</p>
                </td>
                <td className="hidden md:table-cell">
                  <Link to={`/leveranser/${s.delivery_id}`} className="text-[15px] leading-snug hover:text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{s.project_name}</Link>
                  <p className="t-meta mt-0.5">{s.delivery_number} · {s.customer_name}</p>
                </td>
                <td className="hidden sm:table-cell">
                  <p className={`text-[15px] tabular leading-snug ${s.planned_week ? "" : "text-muted-foreground"}`}>{s.planned_week ? weekLabel(s.planned_week) : "Ikke planlagt"}</p>
                  <p className="t-meta mt-0.5">{s.assigned_to || "Ikke tildelt"}</p>
                </td>
                <td><StateChip tone={toneForServiceStatus(s.status)} strong>{SERVICE_STATUS_LABELS[s.status]}</StateChip></td>
                <td className="hidden text-[14px] text-muted-foreground xl:table-cell">{BILLABLE_LABELS[s.billable] || s.billable}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
